import type { DateIso, Phase } from '../../types/domain';
import { addDays, contractMonthsElapsed, currentContractYear, dateFromIso, isoStr } from '../dates';

export type CarryOverKind = 'correctif' | 'evolutif';

// Volets concernés par le report : le correctif toujours, l'évolutif pour une évolutive.
export const carryOverKinds = (phase: Phase): CarryOverKind[] =>
  phase.facturation === 'maintenance_evolutive' ? ['correctif', 'evolutif'] : ['correctif'];

// Report reçu par l'année de contrat qui commence le `from` (unité du quota du volet).
export function carriedInto(phase: Phase, from: DateIso, kind: CarryOverKind): number {
  return Number(phase.maintenanceReports?.[from]?.[kind]) || 0;
}

export interface CarryOverPrompt {
  yearTo: DateIso;
  nextFrom: DateIso;
  /** Report déjà saisi pour l'année suivante, null tant qu'un volet manque. */
  saved: Partial<Record<CarryOverKind, number>> | null;
}

/* Saisie du report de maintenance, correctif et évolutif (arbitrage du 14/10/2026) : pendant le
   dernier mois de l'année de contrat en cours, si le contrat continue (reconduction tacite sans
   date de fin, ou fin après cette année). Le plafond est calculé hors de l'outil : saisie libre. */
export function carryOverPrompt(phase: Phase, todayIso: DateIso): CarryOverPrompt | null {
  const year = currentContractYear(phase.dateDebut, phase.dateFin, todayIso);
  if (!year || (phase.dateFin && phase.dateFin <= year.to)) return null;
  if (contractMonthsElapsed(year.from, year.to, todayIso) < year.months) return null;
  const dayAfter = isoStr(addDays(dateFromIso(year.to), 1));
  const next = currentContractYear(phase.dateDebut, phase.dateFin, dayAfter);
  if (!next) return null;
  const saved = phase.maintenanceReports?.[next.from];
  const complete = !!saved && carryOverKinds(phase).every((k) => saved[k] != null);
  return { yearTo: year.to, nextFrom: next.from, saved: complete ? saved! : null };
}
